import { HttpClient } from '@angular/common/http';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoDirective } from '@jsverse/transloco';
import { AUTHENTICATION_SERVICE } from '@processpuzzle/auth/domain';
import { ORG_ADMIN_ORG_KEY } from '@processpuzzle/org-admin';
import { RUNTIME_CONFIGURATION, serviceRootOf } from '@processpuzzle/util';
import { catchError, of } from 'rxjs';

/** One organization of the signed-in account, as the custom backend's `GET /me/organizations` answers it. */
export interface MemberOrganization {
  orgKey: string;
  name: string;
}

/**
 * Moves between the signed-in customer's applications: one person is one login, and a customer with
 * a second subscription is a member of a second organization under the same account.
 *
 * **The list comes from the custom backend, not from the token.** This application asks Keycloak for
 * `organization:<orgKey>` (see `main.ts`), so its token names the current organization only — the
 * workflow roles in that claim count only while it names exactly one. `GET /me/organizations` asks
 * Keycloak for the account's memberships instead.
 *
 * **An icon button with a menu, like the language selector beside it**, with a tooltip naming the
 * application this page is; the menu marks it with a check.
 *
 * **Hidden with one organization**, which is every customer with one subscription, and for a visitor
 * who is not signed in: the call needs a token, and without one there is nothing to switch.
 *
 * **Switching is a full navigation**, never a router link. The application bootstraps once per
 * organization — the routes, the service roots and the token's organization scope are all built
 * from the key in `main.ts` — so the next organization has to start from there. Single sign-on makes
 * the new token a redirect away, not a second login.
 */
@Component({
  selector: 'app-organization-switcher',
  imports: [MatIcon, MatIconButton, MatMenu, MatMenuItem, MatMenuTrigger, MatTooltip, TranslocoDirective],
  template: `
    @if (switchable()) {
      <ng-container *transloco="let t; prefix: 'organizationSwitcher'">
        <button
          mat-icon-button
          type="button"
          test-id="organization-switcher"
          [matMenuTriggerFor]="organizationMenu"
          [matTooltip]="t('tooltip', { name: current()?.name ?? currentKey })"
          [attr.aria-label]="t('label')"
        >
          <mat-icon>apps</mat-icon>
        </button>
        <mat-menu #organizationMenu="matMenu">
          @for (organization of organizations(); track organization.orgKey) {
            <button mat-menu-item type="button" [attr.test-id]="'organization-switcher-' + organization.orgKey" (click)="open(organization.orgKey)">
              <mat-icon>{{ organization.orgKey === currentKey ? 'check' : '' }}</mat-icon>
              <span>{{ organization.name }}</span>
              <span class="organization-switcher__key">{{ organization.orgKey }}</span>
            </button>
          }
        </mat-menu>
      </ng-container>
    }
  `,
  styles: [
    `
      .organization-switcher__key {
        margin-left: 8px;
        font-family: monospace;
        font-size: 0.8rem;
        opacity: 0.7;
      }
    `,
  ],
})
export class OrganizationSwitcherComponent {
  readonly currentKey = inject(ORG_ADMIN_ORG_KEY);

  private readonly authService = inject(AUTHENTICATION_SERVICE);
  private readonly httpClient = inject(HttpClient);
  private readonly root = serviceRootOf(inject(RUNTIME_CONFIGURATION), 'ORG_ADMIN_SERVICE_ROOT');

  readonly organizations = signal<MemberOrganization[]>([]);
  readonly switchable = computed(() => this.organizations().length > 1);
  readonly current = computed(() => this.organizations().find((organization) => organization.orgKey === this.currentKey));

  /** Asked at most once per page: a failed or one-organization answer must not be asked again. */
  private asked = false;

  constructor() {
    // Asked once the session is known, and only for a signed-in customer in an organization's URL.
    // A failure hides the switcher: it is a convenience, and the page works without it. The backend
    // answers the token's own organization when Keycloak cannot be asked, so a failure here is a
    // transport one, which the central interceptor reports like any other.
    effect(() => {
      if (this.asked || !this.authService.isAuthenticated() || !this.currentKey) return;
      this.asked = true;
      this.httpClient
        .get<MemberOrganization[]>(`${this.root}/me/organizations`)
        .pipe(catchError(() => of([] as MemberOrganization[])))
        .subscribe((organizations) => this.organizations.set(Array.isArray(organizations) ? organizations : []));
    });
  }

  /** A full page load of the other organization's home, under this application's `<base href>`. */
  open(orgKey: string): void {
    if (orgKey === this.currentKey) return;
    this.navigateTo(new URL(`${encodeURIComponent(orgKey)}/home`, document.baseURI).href);
  }

  /** The page load itself, apart so that a test can observe it without leaving the page. */
  protected navigateTo(url: string): void {
    window.location.assign(url);
  }
}
